import { useState, useEffect, useCallback, Fragment } from 'react';
import { fetchAdminActivityLogs, fetchAdminAdminLogs } from '../services/adminApi';

const MODULE_LABELS = {
    auth:           'Prihlásenie',
    profile:        'Profil',
    settings:       'Nastavenia',
    billing:        'Fakturácia',
    branch:         'Prevádzky',
    client:         'Klienti',
    evidenceSheet:  'Evidenčné listy',
    announcement:   'Ohlásenia',
    container:      'Nádoby',
    warehouse:      'Sklad',
    consent:        'Súhlasy',
    operationalLog: 'Prevádzkový denník',
    ppwr:           'PPWR',
};

const MODULE_OPTIONS = [
    { value: '', label: 'Všetky moduly' },
    ...Object.entries(MODULE_LABELS).map(([value, label]) => ({ value, label })),
];

const ADMIN_ACTION_LABELS = {
    TENANT_BLOCKED:   'Blokovaný subjekt',
    TENANT_UNBLOCKED: 'Odblokovaný subjekt',
    LICENSE_EXTENDED: 'Predĺžená licencia',
    OWNER_CHANGED:    'Zmenený vlastník',
    PAYMENT_DELETED:  'Vymazaná platba',
    USER_UPDATED:     'Upravený používateľ',
};

const ADMIN_ACTION_OPTIONS = [
    { value: '', label: 'Všetky akcie' },
    ...Object.entries(ADMIN_ACTION_LABELS).map(([value, label]) => ({ value, label })),
];

const toDateStr = (d) => d.toISOString().slice(0, 10);

const QUARTER_ENDS = [2, 5, 8, 11];

const PRESETS = [
    {
        key: 'today', label: 'Dnes',
        range: () => { const t = toDateStr(new Date()); return [t, t]; },
    },
    {
        key: 'week', label: 'Týždeň',
        range: () => {
            const to = new Date();
            const from = new Date(to);
            from.setDate(from.getDate() - 6);
            return [toDateStr(from), toDateStr(to)];
        },
    },
    {
        key: 'month', label: 'Mesiac',
        range: () => {
            const now = new Date();
            return [toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)), toDateStr(now)];
        },
    },
    ...([1, 2, 3, 4].map(q => ({
        key: `q${q}`, label: `Q${q}`,
        range: () => {
            const y = new Date().getFullYear();
            const startMonth = (q - 1) * 3;
            const endMonth = QUARTER_ENDS[q - 1];
            const endDay = new Date(y, endMonth + 1, 0).getDate();
            return [toDateStr(new Date(y, startMonth, 1)), toDateStr(new Date(y, endMonth, endDay))];
        },
    }))),
    {
        key: 'year', label: 'Rok',
        range: () => {
            const now = new Date();
            return [toDateStr(new Date(now.getFullYear(), 0, 1)), toDateStr(now)];
        },
    },
];

const PAGE_SIZE = 50;

const formatDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const pluralRecords = (n) => {
    if (n === 1) return '1 záznam';
    if (n < 5) return `${n} záznamy`;
    return `${n} záznamov`;
};

const getPageWindow = (current, total) => {
    if (total <= 1) return [];
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const result = [1];
    if (current > 3) result.push('...');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let p = start; p <= end; p++) result.push(p);
    if (current < total - 2) result.push('...');
    result.push(total);
    return result;
};

export default function Logs() {
    const [activeTab, setActiveTab] = useState('activity');

    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [moduleFilter, setModuleFilter] = useState('');
    const [actionFilter, setActionFilter] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [activePreset, setActivePreset] = useState(null);
    const [page, setPage] = useState(1);

    const [expandedId, setExpandedId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                search: search || undefined,
                from: from || undefined,
                to: to || undefined,
                page,
                limit: PAGE_SIZE,
            };
            if (activeTab === 'activity') {
                if (moduleFilter) params.module = moduleFilter;
            } else {
                if (actionFilter) params.action = actionFilter;
            }
            const fn = activeTab === 'activity' ? fetchAdminActivityLogs : fetchAdminAdminLogs;
            const res = await fn(params);
            setLogs(res.data.data);
            setTotal(res.data.total);
            setPages(res.data.pages);
        } catch {
            setError('Nepodarilo sa načítať logy.');
        } finally {
            setLoading(false);
        }
    }, [activeTab, search, moduleFilter, actionFilter, from, to, page]);

    useEffect(() => {
        const timer = setTimeout(load, search ? 400 : 0);
        return () => clearTimeout(timer);
    }, [load, search]);

    const switchTab = (tab) => {
        if (tab === activeTab) return;
        setActiveTab(tab);
        setPage(1);
        setExpandedId(null);
        setSearch('');
        setModuleFilter('');
        setActionFilter('');
        setFrom('');
        setTo('');
        setActivePreset(null);
    };

    const applyPreset = (preset) => {
        const [f, t] = preset.range();
        setFrom(f);
        setTo(t);
        setActivePreset(preset.key);
        setPage(1);
    };

    const handleFromChange = (val) => { setFrom(val); setActivePreset(null); setPage(1); };
    const handleToChange   = (val) => { setTo(val);   setActivePreset(null); setPage(1); };

    const handleReset = () => {
        setSearch('');
        setModuleFilter('');
        setActionFilter('');
        setFrom('');
        setTo('');
        setActivePreset(null);
        setPage(1);
    };

    const hasFilters = search || moduleFilter || actionFilter || from || to;

    const pageWindow = getPageWindow(page, pages);

    return (
        <div className="adm-page">
            <div className="adm-page-header">
                <h1 className="adm-page-title">Logy</h1>
            </div>

            <div className="adm-tabs" style={{ padding: '0 0 0 0', marginBottom: 20 }}>
                <button
                    className={`adm-tab${activeTab === 'activity' ? ' adm-tab--active' : ''}`}
                    onClick={() => switchTab('activity')}
                >
                    Aktivita používateľov
                </button>
                <button
                    className={`adm-tab${activeTab === 'admin' ? ' adm-tab--active' : ''}`}
                    onClick={() => switchTab('admin')}
                >
                    Admin akcie
                </button>
            </div>

            <div className="adm-filters" style={{ flexWrap: 'wrap', gap: 8 }}>
                <input
                    className="adm-filter-input"
                    placeholder={activeTab === 'activity' ? 'Hľadať podľa používateľa alebo entity...' : 'Hľadať podľa admina alebo cieľa...'}
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                />
                {activeTab === 'activity' ? (
                    <select
                        className="adm-filter-select"
                        value={moduleFilter}
                        onChange={e => { setModuleFilter(e.target.value); setPage(1); }}
                    >
                        {MODULE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                ) : (
                    <select
                        className="adm-filter-select"
                        value={actionFilter}
                        onChange={e => { setActionFilter(e.target.value); setPage(1); }}
                    >
                        {ADMIN_ACTION_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                )}

                <div style={{ display: 'flex', gap: 4 }}>
                    {PRESETS.map(preset => (
                        <button
                            key={preset.key}
                            className={`adm-page-btn${activePreset === preset.key ? ' adm-page-btn--active' : ''}`}
                            onClick={() => applyPreset(preset)}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>

                <input type="date" className="adm-filter-input" value={from} onChange={e => handleFromChange(e.target.value)} max={to || undefined} />
                <input type="date" className="adm-filter-input" value={to}   onChange={e => handleToChange(e.target.value)}   min={from || undefined} />

                {hasFilters && (
                    <button className="adm-page-btn" onClick={handleReset}>Zrušiť filtre</button>
                )}
            </div>

            {error && <div className="adm-error" style={{ marginBottom: 20 }}>{error}</div>}

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                    <span className="adm-spinner" />
                </div>
            ) : logs.length === 0 ? (
                <div className="adm-empty">Žiadne záznamy nevyhovujú filtrom.</div>
            ) : (
                <div className="adm-table-wrap">
                    {activeTab === 'activity' ? (
                        <table className="adm-table">
                            <thead>
                                <tr>
                                    <th>Dátum</th>
                                    <th>Subjekt</th>
                                    <th>Používateľ</th>
                                    <th>Modul</th>
                                    <th>Akcia</th>
                                    <th>Entita</th>
                                    <th>Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => {
                                    const isExpanded = expandedId === log._id;
                                    const hasDetail = log.detail && Object.keys(log.detail).length > 0;
                                    return (
                                        <Fragment key={log._id}>
                                            <tr
                                                className={hasDetail ? 'adm-row--clickable' : undefined}
                                                onClick={hasDetail ? () => setExpandedId(isExpanded ? null : log._id) : undefined}
                                            >
                                                <td className="adm-td-mono" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDateTime(log.createdAt)}</td>
                                                <td className="adm-td-primary">{log.tenant?.companyName ?? '—'}</td>
                                                <td>{log.userName || '—'}</td>
                                                <td>
                                                    <span className="adm-badge adm-badge--gray">
                                                        {MODULE_LABELS[log.module] ?? log.module}
                                                    </span>
                                                </td>
                                                <td className="adm-td-mono" style={{ fontSize: 12 }}>{log.action}</td>
                                                <td style={{ color: 'var(--adm-text-2)', fontSize: 13 }}>{log.entityName || '—'}</td>
                                                <td>
                                                    {hasDetail && (
                                                        <span style={{ color: 'var(--adm-accent)', fontSize: 12, cursor: 'pointer' }}>
                                                            {isExpanded ? '▲' : '▼'}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan={7} style={{ padding: '8px 16px', background: 'var(--adm-bg-2)' }}>
                                                        <pre style={{ margin: 0, fontSize: 12, color: 'var(--adm-text-2)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                                            {JSON.stringify(log.detail, null, 2)}
                                                        </pre>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <table className="adm-table">
                            <thead>
                                <tr>
                                    <th>Dátum</th>
                                    <th>Admin</th>
                                    <th>Akcia</th>
                                    <th>Cieľ</th>
                                    <th>Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => {
                                    const isExpanded = expandedId === log._id;
                                    const hasDetail = log.detail && Object.keys(log.detail).length > 0;
                                    return (
                                        <Fragment key={log._id}>
                                            <tr
                                                className={hasDetail ? 'adm-row--clickable' : undefined}
                                                onClick={hasDetail ? () => setExpandedId(isExpanded ? null : log._id) : undefined}
                                            >
                                                <td className="adm-td-mono" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDateTime(log.createdAt)}</td>
                                                <td className="adm-td-primary">{log.adminName || '—'}</td>
                                                <td>
                                                    <span className="adm-badge adm-badge--gray">
                                                        {ADMIN_ACTION_LABELS[log.action] ?? log.action}
                                                    </span>
                                                </td>
                                                <td style={{ color: 'var(--adm-text-2)', fontSize: 13 }}>
                                                    {log.targetName || '—'}
                                                    {log.targetType && (
                                                        <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--adm-text-3)' }}>
                                                            ({log.targetType === 'tenant' ? 'subjekt' : 'používateľ'})
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {hasDetail && (
                                                        <span style={{ color: 'var(--adm-accent)', fontSize: 12, cursor: 'pointer' }}>
                                                            {isExpanded ? '▲' : '▼'}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan={5} style={{ padding: '8px 16px', background: 'var(--adm-bg-2)' }}>
                                                        <pre style={{ margin: 0, fontSize: 12, color: 'var(--adm-text-2)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                                            {JSON.stringify(log.detail, null, 2)}
                                                        </pre>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                    <div className="adm-table-footer">
                        <span>{pluralRecords(total)}</span>
                        {pages > 1 && (
                            <div className="adm-pagination">
                                <button className="adm-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</button>
                                {pageWindow.map((p, i) => p === '...' ? (
                                    <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: 'var(--adm-text-3)' }}>…</span>
                                ) : (
                                    <button
                                        key={p}
                                        className={`adm-page-btn${p === page ? ' adm-page-btn--active' : ''}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                ))}
                                <button className="adm-page-btn" onClick={() => setPage(p => p + 1)} disabled={page === pages}>›</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
